/**
 * Pure translation from a MediaPipe `FaceLandmarker` result into the core's
 * `EyeSample` shape. Everything here is a plain function over a structural
 * subset of `FaceLandmarkerResult` (see `FaceResultInput` below) — no
 * `@mediapipe/tasks-vision` types are imported, so `test/client/gaze.test.ts`
 * can build tiny fake results without a browser or the real model.
 * `client/face/landmarker.ts` calls `toEyeSample` with the real result value,
 * which satisfies this narrower shape structurally.
 *
 * SIGN CONVENTION (read before touching this file):
 * `gaze.x` is defined "screen-right positive": a player glancing toward the
 * right edge of their monitor should produce a positive x, matching
 * `src/types.ts`'s `EyeSample.gaze` doc comment. Getting there requires two
 * *different* corrections depdepending on which signal is available:
 *
 * 1. Blendshapes (`eyeLookOut*`/`eyeLookIn*`) are anatomical: "out" means
 *    away from the *that eye's own* nose side, so it flips between the two
 *    eyes for one physical gaze direction. Looking toward the player's own
 *    real right — which, facing the screen dead-on, IS screen-right —
 *    drives the right eye outward (`eyeLookOutRight` up) and the left eye
 *    inward (`eyeLookInLeft` up). A naive mean of `(out - in)` per eye
 *    cancels to ~0 for real gaze shifts (it measures vergence, not
 *    direction); the two eyes have to be combined with a per-eye sign so
 *    they add instead of cancelling (see `gazeFromBlendshapes`).
 * 2. The iris/eye-corner fallback works in raw camera-frame pixel space,
 *    which is a single shared x-axis for the whole face — no per-eye flip.
 *    But that axis is the *unmirrored* camera frame: the player faces the
 *    camera, so the player's real right side of their body lands on the
 *    camera's LEFT (small-x) side of the frame — the classic face-to-face
 *    mirroring. So looking screen-right shifts both irises toward smaller
 *    raw x, and this path negates once, globally, to flip that back to
 *    "screen-right positive" (see `gazeFromLandmarks`).
 *
 * `gaze.y` has no such crossing (both eyes' up/down sense agree), so it's a
 * plain mean of `(down - up)` with no sign correction either path.
 */

import type { Vec2 } from "../../src";

export interface BlendshapeCategory {
  readonly categoryName: string;
  readonly score: number;
}

export interface Landmark3D {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** `Matrix` from `@mediapipe/tasks-vision`: `rows`x`columns`, column-major `data`. */
export interface TransformMatrix {
  readonly rows: number;
  readonly columns: number;
  readonly data: readonly number[];
}

/** Structural subset of `FaceLandmarkerResult` this module needs. */
export interface FaceResultInput {
  readonly faceLandmarks: ReadonlyArray<ReadonlyArray<Landmark3D>>;
  readonly faceBlendshapes: ReadonlyArray<{
    readonly categories: ReadonlyArray<BlendshapeCategory>;
  }>;
  readonly facialTransformationMatrixes: ReadonlyArray<TransformMatrix>;
}

export interface EyeSampleLike {
  t: number;
  gaze: Vec2;
  blinkL: number;
  blinkR: number;
  headRot: { yaw: number; pitch: number; roll: number };
  facePresent: boolean;
}

/** Side channel for which path `toEyeSample` took on its most recent call (module-level
 * per this file's own doc comment on `toEyeSample`, since `EyeSample`/`src/types.ts` is
 * core-owned and out of scope here) — read by `client/render/canvas.ts`'s HUD via
 * `getLastGazePath`. */
let lastGazePath: "blendshapes" | "iris" = "blendshapes";

export function getLastGazePath(): "blendshapes" | "iris" {
  return lastGazePath;
}

function categoryScore(categories: ReadonlyArray<BlendshapeCategory>, name: string): number {
  for (const c of categories) {
    if (c.categoryName === name) return c.score;
  }
  return 0;
}

/** `-0` is numerically equal to `0` but trips `toEqual`/`Object.is`-based assertions and prints ugly in the HUD — normalize it away at every output boundary. */
function clean(n: number): number {
  return n === 0 ? 0 : n;
}

/** See the sign-convention doc comment at the top of this file. */
function gazeFromBlendshapes(categories: ReadonlyArray<BlendshapeCategory>): Vec2 {
  const outR = categoryScore(categories, "eyeLookOutRight");
  const inR = categoryScore(categories, "eyeLookInRight");
  const outL = categoryScore(categories, "eyeLookOutLeft");
  const inL = categoryScore(categories, "eyeLookInLeft");
  const downR = categoryScore(categories, "eyeLookDownRight");
  const upR = categoryScore(categories, "eyeLookUpRight");
  const downL = categoryScore(categories, "eyeLookDownLeft");
  const upL = categoryScore(categories, "eyeLookUpLeft");

  const rightness = (outR - inR + (inL - outL)) / 2;
  const downness = (downR - upR + (downL - upL)) / 2;
  return { x: clean(rightness), y: clean(downness) };
}

/**
 * Fallback gaze estimate when blendshapes are unavailable: iris centre
 * relative to each eye's outer/inner corner landmarks, in raw (unmirrored)
 * normalized image space. Landmark indices are MediaPipe's canonical
 * 478-point refined face mesh: 33/133 = right eye outer/inner corner,
 * 468 = right iris centre; 362/263 = left eye inner/outer corner,
 * 473 = left iris centre. No vertical corner landmarks are specified for
 * this fallback, so `y` reuses the horizontal eye-corner span to normalize
 * — a coarser approximation than the blendshape path, acceptable only
 * because this path is itself already a degraded fallback.
 */
function gazeFromLandmarks(landmarks: ReadonlyArray<Landmark3D>): Vec2 {
  const rOuter = landmarks[33];
  const rInner = landmarks[133];
  const rIris = landmarks[468];
  const lInner = landmarks[362];
  const lOuter = landmarks[263];
  const lIris = landmarks[473];

  if (
    rOuter === undefined ||
    rInner === undefined ||
    rIris === undefined ||
    lInner === undefined ||
    lOuter === undefined ||
    lIris === undefined
  ) {
    return { x: 0, y: 0 };
  }

  const rWidth = Math.abs(rOuter.x - rInner.x) || 1;
  const lWidth = Math.abs(lOuter.x - lInner.x) || 1;
  const rCenterX = (rOuter.x + rInner.x) / 2;
  const lCenterX = (lOuter.x + lInner.x) / 2;
  const rCenterY = (rOuter.y + rInner.y) / 2;
  const lCenterY = (lOuter.y + lInner.y) / 2;

  const rDx = (rIris.x - rCenterX) / rWidth;
  const lDx = (lIris.x - lCenterX) / lWidth;
  const rDy = (rIris.y - rCenterY) / rWidth;
  const lDy = (lIris.y - lCenterY) / lWidth;

  // Negate once, globally: raw image x is the unmirrored camera frame, where
  // screen-right (the player's real right) shows up as smaller x (see the
  // module doc comment). y needs no such flip — image y already increases
  // downward, matching the "down positive" contract directly.
  const x = -((rDx + lDx) / 2);
  const y = (rDy + lDy) / 2;
  return { x: clean(x), y: clean(y) };
}

/**
 * `data` is a flattened column-major `rows`x`columns` matrix — for a facial
 * transformation matrix that's 4x4, so `M[row][col] = data[col*rows + row]`.
 * Decomposes the rotation part with the same 'XYZ' intrinsic Tait-Bryan
 * convention Three.js's `Euler.setFromRotationMatrix(m, "XYZ")` uses (a
 * standard, well-documented algorithm): x -> pitch, y -> yaw, z -> roll,
 * all in radians. Only used for the *rate* of head motion
 * (`headMotionDegPerS`), so the exact convention matters less than it being
 * continuous and consistent frame to frame.
 *
 * MediaPipe's facial transformation matrix carries a uniform scale baked
 * into the rotation submatrix (it's a full model-to-camera transform, not a
 * bare rotation). `asin` is non-linear, so feeding a scaled `m13` straight
 * in blows up: a 2x scale turns a real 30° yaw into `asin(2*sin(30°))` =
 * `asin(1.0)` = 90°. Each column is normalized to unit length first to
 * recover the true rotation before any angle is extracted.
 */
export function matrixToEuler(matrix: TransformMatrix): {
  yaw: number;
  pitch: number;
  roll: number;
} {
  const rows = matrix.rows > 0 ? matrix.rows : 4;
  const data = matrix.data;
  const at = (row: number, col: number): number => data[col * rows + row] ?? 0;

  const m11Raw = at(0, 0);
  const m21Raw = at(1, 0);
  const m31Raw = at(2, 0);
  const m12Raw = at(0, 1);
  const m22Raw = at(1, 1);
  const m32Raw = at(2, 1);
  const m13Raw = at(0, 2);
  const m23Raw = at(1, 2);
  const m33Raw = at(2, 2);

  // Column lengths equal the (uniform) scale factor baked in by MediaPipe;
  // `|| 1` guards a degenerate zero-length column (leaves it unscaled
  // rather than dividing by zero).
  const len0 = Math.hypot(m11Raw, m21Raw, m31Raw) || 1;
  const len1 = Math.hypot(m12Raw, m22Raw, m32Raw) || 1;
  const len2 = Math.hypot(m13Raw, m23Raw, m33Raw) || 1;

  const m11 = m11Raw / len0;
  const m12 = m12Raw / len1;
  const m22 = m22Raw / len1;
  const m23 = m23Raw / len2;
  const m32 = m32Raw / len1;
  const m33 = m33Raw / len2;
  const m13 = m13Raw / len2;

  const clampedM13 = Math.max(-1, Math.min(1, m13));
  const yaw = Math.asin(clampedM13);

  let pitch: number;
  let roll: number;
  if (Math.abs(m13) < 0.9999999) {
    pitch = Math.atan2(-m23, m33);
    roll = Math.atan2(-m12, m11);
  } else {
    // Gimbal lock: pitch/roll aren't individually observable, so roll is
    // pinned to 0 and everything folds into pitch — same convention Three.js
    // uses at this same singularity.
    pitch = Math.atan2(m32, m22);
    roll = 0;
  }

  return { yaw: clean(yaw), pitch: clean(pitch), roll: clean(roll) };
}

/**
 * `facePresent = faceLandmarks.length > 0` per the task contract. Blink
 * and head rotation have no documented fallback when blendshapes/the
 * transformation matrix are missing — both default to "neutral" (eyes
 * open, no rotation) rather than guessing, which just means a momentarily
 * face-present-but-blendshape-less frame won't spuriously read as a blink
 * or a head turn.
 */
export function toEyeSample(result: FaceResultInput, now: number): EyeSampleLike {
  const landmarks = result.faceLandmarks[0] ?? [];
  const facePresent = result.faceLandmarks.length > 0 && landmarks.length > 0;
  const categories = result.faceBlendshapes[0]?.categories ?? [];
  const hasBlendshapes = categories.length > 0;
  const matrix = result.facialTransformationMatrixes[0];

  const gaze = hasBlendshapes ? gazeFromBlendshapes(categories) : gazeFromLandmarks(landmarks);
  lastGazePath = hasBlendshapes ? "blendshapes" : "iris";
  const blinkL = hasBlendshapes ? categoryScore(categories, "eyeBlinkLeft") : 0;
  const blinkR = hasBlendshapes ? categoryScore(categories, "eyeBlinkRight") : 0;
  const headRot = matrix !== undefined ? matrixToEuler(matrix) : { yaw: 0, pitch: 0, roll: 0 };

  return { t: now, gaze, blinkL, blinkR, headRot, facePresent };
}
